import { useMemo, useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { getEnums } from '../../api/enums.api';
import { setEnums } from '../../store/enums.slice';
import SideBar from '../../layout/side-bar';
import Header from '../../layout/header';
import { LoadingPartially } from '@jadwalh/ui';
import { Suspense } from 'react';
import { LayoutContainer, LayoutWrapper } from '../../layout/styles';
import instance from '../../axios-instance';
import { storeDataInIndexedDB } from '@jadwalh/utils';
import { getProfile } from '../../api/profile.api';
import GlobalChatMenu from '../../components/GlobalChatMenu';
import { removeAuthToken } from '../../utils/auth-token';

export default function Home() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [matches, setMatches] = useState(
    window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    window
      .matchMedia('(min-width: 768px)')
      .addEventListener('change', (e) => setMatches(e.matches));
  }, []);

  const hideSideBar = useMemo(() => {
    const firstPathSegment = pathname.split('/')[1];
    const isSettingsPage = firstPathSegment.includes('settings') && matches;
    const isPlansRoute = pathname === '/plans' || pathname === '/plans/summary' || pathname === '/subscription-suspended';
    return isSettingsPage || isPlansRoute;
  }, [pathname, matches]);

  useQuery({
    queryKey: ['enums'],
    queryFn: () => getEnums(),
    onSuccess: (data) => {
      const updatedData: any = [];
      for (const key in data.data) {
        if (Object.hasOwnProperty.call(data.data, key)) {
          updatedData[key] = Object.entries(data.data[key]).map(
            ([label, value]) => ({
              label,
              value: value.toString(),
            })
          );
        }
      }
      dispatch(setEnums(updatedData));
    },
    staleTime: Infinity,
  });

  const { data: languageData } = useQuery({
    queryKey: ['languageData'],
    queryFn: () => instance.get('/app/languages'),
    select: (res: any): { value: string; label: string }[] =>
      res.data.data.map((item: any) => ({
        value: item.id.toString(),
        label: item.name,
      })),
  });

  const { data: countryData } = useQuery({
    queryKey: ['countryData'],
    queryFn: () => instance.get('/app/countries'),
    select: (res: any): { value: string; label: string }[] =>
      res.data.data.map((item: any) => ({
        value: item.id.toString(),
        label: item.name,
      })),
    refetchOnWindowFocus: false,
  });

  // get Profile
  const { data } = useQuery({
    queryKey: ['profile'],
    queryFn: () => getProfile(),
    onSuccess: (data) => {
      if (data.status === 0) {
        localStorage.removeItem('email');
        removeAuthToken();
        localStorage.removeItem('isNew');
        window.location.href = '/login';
      }
    },
    refetchInterval: (data) => {
      // Poll every 24 hours if status is not 1
      return data?.status !== 1 ? 86400000 : false;
    },
  });

  if (languageData && countryData)
    storeDataInIndexedDB(languageData, countryData);

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: '#1A1A1A' }}>
      <Header />
      {/* <TrialBanner /> */}

      <LayoutWrapper>
        {!hideSideBar && <SideBar />}
        <LayoutContainer id="LayoutContainer">
          <main>
            <Suspense fallback={<LoadingPartially />}>
              <Outlet />
            </Suspense>
          </main>
        </LayoutContainer>
      </LayoutWrapper>

      {/* Global Chat Menu - appears on all pages */}
      <GlobalChatMenu />
    </div>
  );
}
